// hooks/useTranslation.js

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const translationCache = new Map();

const useTranslation = (text, options = {}) => {
  const {
    translateText,
    currentLanguage,
    isTranslating,
    languageChangeCount,
  } = useLanguage();

  const {
    skipCache = false,
    fallbackText = null,
    debounceMs = 0,
  } = options;

  const [translatedText, setTranslatedText] = useState(
    typeof text === 'string' ? text : ''
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const mountedRef = useRef(false);

  // Component mount/unmount
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const cacheKey = `${text}|${currentLanguage}`;

  const translate = useCallback(async () => {
    if (!mountedRef.current) return;

    // Ignore invalid text
    if (typeof text !== 'string' || text.length === 0) {
      setTranslatedText('');
      return;
    }

    // English = original text
    if (currentLanguage === 'en') {
      setTranslatedText(text);
      setError(null);
      setIsLoading(false);
      return;
    }

    // Cache
    if (!skipCache && translationCache.has(cacheKey)) {
      setTranslatedText(translationCache.get(cacheKey));
      setError(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const result = await translateText(text);

      console.log(
        'Translate:',
        text,
        '=>',
        result,
        'type:',
        typeof result
      );

      // Always return a string
      const safeResult =
        typeof result === 'string'
          ? result
          : fallbackText ?? text;

      if (!mountedRef.current) return;

      if (!skipCache) {
        if (translationCache.size > 500) {
          const firstKey = translationCache.keys().next().value;
          translationCache.delete(firstKey);
        }

        translationCache.set(cacheKey, safeResult);
      }

      setTranslatedText(safeResult);
    } catch (err) {
      console.error('Translation error:', err);

      if (!mountedRef.current) return;

      setError(err);
      setTranslatedText(fallbackText ?? text);
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    text,
    currentLanguage,
    translateText,
    skipCache,
    fallbackText,
    cacheKey,
  ]);

  // Retranslate whenever language changes
  useEffect(() => {
    if (debounceMs > 0) {
      const timer = setTimeout(() => {
        translate();
      }, debounceMs);

      return () => clearTimeout(timer);
    }

    translate();
  }, [translate, languageChangeCount, debounceMs]);

  const retry = useCallback(() => {
    translate();
  }, [translate]);

  const clearCache = useCallback(() => {
    translationCache.clear();
  }, []);

  return {
    translatedText,
    isLoading,
    error,
    retry,
    clearCache,
    isTranslating: isLoading || isTranslating,
  };
};

export default useTranslation;